import React, { useState, useEffect, useContext } from "react";
import { ContentContext } from "../../App";

const ContentManager = () => {
  const { content, updateContent, loading } = useContext(ContentContext);
  const [editingContent, setEditingContent] = useState({});
  const [saving, setSaving] = useState({});

  const contentTypes = [
    { key: 'hero_title', label: 'Título Principal', description: 'Título que aparece en la sección hero' },
    { key: 'hero_subtitle', label: 'Subtítulo Hero', description: 'Subtítulo del hero' },
    { key: 'hero_description', label: 'Descripción Hero', description: 'Descripción en la sección hero' },
    { key: 'about_title', label: 'Título Sobre Nosotros', description: 'Título de la sección sobre nosotros' },
    { key: 'about_description', label: 'Descripción Sobre Nosotros', description: 'Descripción de la empresa' },
    { key: 'contact_title', label: 'Título Contacto', description: 'Título de la sección de contacto' },
    { key: 'contact_description', label: 'Descripción Contacto', description: 'Descripción del contacto' },
    { key: 'company_name', label: 'Nombre de la Empresa', description: 'Nombre que aparece en header y footer' },
    { key: 'company_email', label: 'Email de Contacto', description: 'Email de contacto de la empresa' },
    { key: 'company_address', label: 'Dirección', description: 'Dirección de la empresa' },
    { key: 'footer_description', label: 'Descripción Footer', description: 'Descripción en el footer' },
  ];

  useEffect(() => {
    // Initialize editing content with current values
    const initialEditingContent = {};
    contentTypes.forEach(type => {
      initialEditingContent[type.key] = content[type.key] || '';
    });
    setEditingContent(initialEditingContent);
  }, [content]);

  const handleEdit = (key, value) => {
    setEditingContent(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await updateContent(key, editingContent[key]);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error al guardar el contenido');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleCancel = (key) => {
    setEditingContent(prev => ({
      ...prev,
      [key]: content[key] || ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Contenido</h1>
        <p className="text-gray-600">Edita los textos que aparecen en la web</p>
      </div>

      <div className="space-y-6">
        {contentTypes.map((type) => (
          <div key={type.key} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{type.label}</h3>
                <p className="text-sm text-gray-500">{type.description}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSave(type.key)}
                  disabled={saving[type.key] || editingContent[type.key] === content[type.key]}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving[type.key] ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={() => handleCancel(type.key)}
                  disabled={saving[type.key]}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </div>

            {type.key.includes('description') || type.key.includes('hero_') ? (
              <textarea
                value={editingContent[type.key] || ''}
                onChange={(e) => handleEdit(type.key, e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={`Ingresa el ${type.label.toLowerCase()}`}
              />
            ) : (
              <input
                type="text"
                value={editingContent[type.key] || ''}
                onChange={(e) => handleEdit(type.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={`Ingresa el ${type.label.toLowerCase()}`}
              />
            )}

            {/* Show current vs new content */}
            {editingContent[type.key] !== content[type.key] && content[type.key] && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>Contenido actual:</strong> {content[type.key]}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Preview Section */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Vista Previa</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-2">
            Los cambios se verán reflejados en la web después de guardar cada sección.
          </p>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Ver Web
          </a>
        </div>
      </div>

      {/* Tips Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">💡 Consejos para el Contenido</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>• <strong>Título Principal:</strong> Debe ser claro y atractivo, idealmente no más de 8 palabras</li>
          <li>• <strong>Descripciones:</strong> Usa un lenguaje claro y directo que conecte con tu audiencia</li>
          <li>• <strong>Información de contacto:</strong> Asegúrate de que email y dirección estén actualizados</li>
          <li>• <strong>Consistencia:</strong> Mantén un tono y estilo coherente en todos los textos</li>
        </ul>
      </div>
    </div>
  );
};

export default ContentManager;